import { FormTemplateFieldConditionalRule } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FieldConditionHelpers } from '../hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from '../types';
import { AddConditionalRuleButton } from './AddConditionalRuleButton';
import { RuleConfiguration } from './RuleConfiguration';

interface ConditionalRulesProps {
  addRuleButtonRef: React.RefObject<HTMLButtonElement>;
  field: EditableFormTemplateField;
  fieldConditionHelpers: FieldConditionHelpers;
  moveFocusToAddRuleButton: () => void;
  fieldsInGroup: EditableFormTemplateField[];
}

export function ConditionalRules({
  addRuleButtonRef,
  field,
  fieldConditionHelpers,
  moveFocusToAddRuleButton,
  fieldsInGroup,
}: ConditionalRulesProps) {
  const {
    fields: {
      push: addConditionalRule,
      remove: deleteConditionalRule,
      value: conditionalRules,
    },
  } =
    useFieldArray<Partial<FormTemplateFieldConditionalRule>>(
      'conditionalRules',
    );

  const onDelete = useCallback(
    (index: number) => {
      const isLastRule = index === conditionalRules.length - 1;
      const willDeleteButtonDisappear = conditionalRules.length <= 2;
      const shouldMoveFocusAfterDelete =
        willDeleteButtonDisappear || isLastRule;

      deleteConditionalRule(index);
      if (shouldMoveFocusAfterDelete) {
        moveFocusToAddRuleButton();
      }
    },
    [conditionalRules.length, deleteConditionalRule, moveFocusToAddRuleButton],
  );

  return (
    <>
      <FieldArray name="conditionalRules">
        {({ fields: conditionalRulesFields }) =>
          conditionalRulesFields.map((ruleName, index) => (
            <RuleConfiguration
              fieldsInGroup={fieldsInGroup}
              fieldConditionHelpers={fieldConditionHelpers}
              key={ruleName}
              deleteConditionalRule={() => onDelete(index)}
              ruleName={ruleName}
              triggerField={field}
            />
          ))
        }
      </FieldArray>
      <AddConditionalRuleButton
        focusCatcherRef={addRuleButtonRef}
        addConditionalRule={addConditionalRule}
      />
    </>
  );
}
